import React, { useState } from 'react';
import { 
  USCISStep1NoticeUpload, 
  USCISStep2SmartBreakdown 
} from './USCISNoticeScanner';
import { 
  USCISStep3ProceedDecision, 
  USCISStep4DocumentUpload 
} from './USCISNoticeScanner2';
import { 
  USCISStep5EvidenceStrategy, 
  USCISStep6DocumentAnalyzer 
} from './USCISNoticeScanner3';
import { 
  USCISStep7GapAnalysis, 
  USCISStep8AffidavitBuilder 
} from './USCISNoticeScanner4';
import { 
  USCISStep10FinalReview 
} from './USCISNoticeScanner5';
import { 
  USCISStep11QualityAssurance, 
  USCISStep12GeneratePacket 
} from './USCISNoticeScanner6';

export const USCISNoticeScannerFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    uploadedNotice: null,
    parsedResults: null,
    breakdown: null,
    proceedDecision: '',
    responseDocuments: [],
    strategyAnswers: {},
    evidenceStrategy: null,
    documentAnalysis: null,
    gapAnalysis: null,
    savedAffidavits: [],
    coverLetter: '',
    checkedItems: {},
    qaResults: null,
    responsePacket: null
  });

  const steps = [
    'Notice Upload',
    'Smart Breakdown of USCIS Request',
    'Proceed With Response?',
    'Document Upload Portal',
    'Evidence Strategy Session',
    'AI Document Analyzer',
    'Evidence Gap Analysis & Prioritization',
    'Affidavit Builder',
    'Final Review & Checklist',
    'Final Quality Assurance',
    'Generate Response Packet'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle special case for step 3 (proceed decision)
  React.useEffect(() => {
    if (formData.proceedDecision === 'no' && currentStep === 2) {
      // Show summary and close or redirect
      setTimeout(() => {
        alert('Analysis summary saved. You can return later to continue response preparation.');
        onClose();
      }, 1000);
    }
  }, [formData.proceedDecision, currentStep]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">📄</span>
              <div>
                <h2 className="text-2xl font-bold text-white">USCIS Notice Scanner</h2>
                <p className="text-gray-400">AI-Powered RFE/NOID Response Generator</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-red-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Deadline Alert (show after step 1) */}
          {currentStep > 0 && formData.parsedResults?.deadline && (
            <div className="bg-red-900/20 border border-red-500 p-3 rounded-lg mb-6">
              <div className="flex items-center">
                <span className="text-red-400 mr-2">⏰</span>
                <span className="text-red-400 font-semibold">
                  Response Deadline: {formData.parsedResults.deadline} 
                  ({formData.parsedResults.response_deadline_days} days remaining)
                </span>
              </div>
            </div>
          )}

          {/* Step Content */}
          {currentStep === 0 && (
            <USCISStep1NoticeUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <USCISStep2SmartBreakdown 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <USCISStep3ProceedDecision 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <USCISStep4DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <USCISStep5EvidenceStrategy 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <USCISStep6DocumentAnalyzer 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <USCISStep7GapAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <USCISStep8AffidavitBuilder 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 8 && (
            <USCISStep10FinalReview 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 9 && (
            <USCISStep11QualityAssurance 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 10 && (
            <USCISStep12GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};